import { Box, Checkbox, TextField, Tooltip, Typography } from "@mui/material";
import { Edit, Delete, NorthEast, ErrorOutline } from "@mui/icons-material";

import { SelectField, TableActions } from "../../components/v3";
import { getBalanceColor } from "../../utils";
import { AccountService } from "../../services";

// Route paths for the worker module live in `./routes.constants` (kept import-free so `authUtils.ts` can
// use them without pulling in the `services` barrel this file needs — see that file for why).
export { WORKER_ROUTES } from "./routes.constants";

export const WorkerTableColumns = [
  { field: "sl", headerName: "Sl No", flex: 0.4, sortable: false },
  {
    field: "name",
    headerName: "Name",
    flex: 1.5,
    sortable: true,
  },
  {
    field: "designation",
    headerName: "Designation",
    flex: 1.5,
    sortable: true,
  },
  {
    field: "primaryPhone",
    headerName: "Phone Number",
    flex: 1.5,
    sortable: false,
  },
  {
    field: "salary",
    headerName: "Salary",
    flex: 1.5,
    sortable: true,
  },
  {
    field: "overtimeHourlyRate",
    headerName: "Overtime (Hourly Rate)",
    flex: 1.5,
    sortable: true,
  },
  {
    field: "formattedBalance",
    headerName: "Balance",
    flex: 1.5,
    sortable: false,
    renderCell: (params) => {
      const value = params.value;
      // using raw balance to determine color, because formatted balance is always formatted for expense accounts (i.e. multiplied by -1)
      const rawBalance = params.row.balance;
      return <Box sx={{ color: getBalanceColor(parseFloat(rawBalance)), display: "flex", alignItems: "center" }}>{value}</Box>;
    },
  },
];

const buildWorkerActionsColumn = (
  handleActions,
  hasEditWork,
  hasDeleteWork,
  hasEditPayment,
  hasDeletePayment,
  isGroupWorker,
) => ({
  field: "actions",
  headerName: "Actions",
  flex: 1,
  sortable: false,
  renderCell: (params) => {
    const { type, meta } = params.row;
    const actions = [];

    if (type === "work" && !meta?.isOldBalance) {
      const canEditWork = hasEditWork && !(params.row.isGroupWork && !isGroupWorker);
      if (canEditWork) {
        actions.push({
          icon: Edit,
          type: "edit",
          label: "Edit",
          onClick: handleActions,
        });
      }

      if (hasDeleteWork) {
        actions.push({
          icon: Delete,
          type: "delete",
          label: "Delete",
          onClick: handleActions,
        });
      }
    } else if (type === "transaction") {
      if (hasEditPayment) {
        actions.push({
          icon: Edit,
          type: "edit",
          label: "Edit",
          onClick: handleActions,
        });
      }

      if (hasDeletePayment) {
        actions.push({
          icon: Delete,
          type: "delete",
          label: "Delete",
          onClick: handleActions,
        });
      }
    }
    return <TableActions item={params} actions={actions} />;
  },
});

export const getStatementTableColumns = (
  handleActions,
  hasEditWork,
  hasDeleteWork,
  hasEditPayment,
  hasDeletePayment,
  selectedFilter,
  isGroupWorker,
) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "date", headerName: "Date", flex: 0.8, sortable: true, sortingOrder: ["desc", "asc", null] },

    { field: "projectName", headerName: "project / account", flex: 1.5, sortable: false },
    { field: "remarks", headerName: "Remarks", flex: 1.5, sortable: false },
    {
      field: "groupWork",
      headerName: "Type",
      flex: 0.7,
      sortable: false,
      renderCell: (params) => {
        if (params.row.type !== "work") return "-";
        return params.value || "Individual";
      },
    },
    {
      field: "work",
      headerName: "Work",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(params.value).toFixed(2)}</span>
        );
      },
    },
    {
      field: "overtime",
      headerName: "Overtime",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(params.value).toFixed(2)}</span>
        );
      },
    },
    {
      field: "amount",
      headerName: "Payment",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "green", display: "flex", alignItems: "center", gap: 4 }}>
            <NorthEast fontSize="small" />₹ {Number(params.value).toFixed(2)}
          </span>
        );
      },
    },
    // { field: "balance", headerName: "Balance", flex: 1 }, // TODO: activate it once the balance is ready
  ];
  if (hasEditWork || hasDeleteWork || hasEditPayment || hasDeletePayment) {
    headers.push(
      buildWorkerActionsColumn(handleActions, hasEditWork, hasDeleteWork, hasEditPayment, hasDeletePayment, isGroupWorker),
    );
  }

  return selectedFilter === "work"
    ? headers.filter((col) => col.field !== "amount")
    : selectedFilter === "transaction"
      ? headers.filter((col) => col.field !== "work" && col.field !== "overtime" && col.field !== "groupWork")
      : headers;
};

export const getLedgerTableColumns = (
  handleActions,
  hasEditWork,
  hasDeleteWork,
  hasEditPayment,
  hasDeletePayment,
  isGroupWorker,
) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "date", headerName: "Date", flex: 0.8, sortable: false },
    { field: "projectName", headerName: "project / account", flex: 1.5, sortable: false },
    { field: "remarks", headerName: "Remarks", flex: 1.5, sortable: false },
    {
      field: "groupWork",
      headerName: "Type",
      flex: 0.7,
      sortable: false,
      renderCell: (params) => {
        if (params.row.type !== "work") return "-";
        return params.value || "Individual";
      },
    },
    {
      field: "work",
      headerName: "Work",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(params.value).toFixed(2)}</span>
        );
      },
    },
    {
      field: "overtime",
      headerName: "Overtime",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(params.value).toFixed(2)}</span>
        );
      },
    },
    {
      field: "amount",
      headerName: "Payment",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "green", display: "flex", alignItems: "center", gap: 4 }}>
            <NorthEast fontSize="small" />₹ {Number(params.value).toFixed(2)}
          </span>
        );
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const rawBalance = params.value;
        if (rawBalance === null || rawBalance === undefined || rawBalance === "") return "-";
        // Invert the stored balance for display, to match formatBalance and the rest of the app.
        const num = -(Number(rawBalance) || 0);
        const formatted = `${num < 0 ? "-" : ""}₹ ${Math.abs(num).toFixed(2)}`;
        return <Box sx={{ color: getBalanceColor(num), display: "flex", alignItems: "center" }}>{formatted}</Box>;
      },
    },
  ];

  if (hasEditWork || hasDeleteWork || hasEditPayment || hasDeletePayment) {
    headers.push(
      buildWorkerActionsColumn(handleActions, hasEditWork, hasDeleteWork, hasEditPayment, hasDeletePayment, isGroupWorker),
    );
  }

  return headers;
};

export const getWorkTableColumns = (hasViewAccount, user, companyAdminId, setRows, selectedAccount) => {
  const headers = [
    { field: "name", headerName: "Name", flex: 1, sortable: false, editable: false },
    {
      field: "salary",
      headerName: "Wage",
      flex: 1,
      editable: true,
      valueSetter: (value, row) => {
        const salary = Number(value) || 0;
        const overtimeHours = Number(row.overtimeHours) || 0;
        const overtimeRate = Number(row.overtimeHourlyRate) || 0;

        const total = salary + overtimeHours * overtimeRate;

        return {
          ...row,
          salary,
          ...(row.paid && { paidAmount: total }),
          salaryError: false, // clear error when editing
        };
      },

      renderCell: (params) => {
        if (params.row.salaryError) {
          return (
            <Tooltip title="Wage is required">
              <ErrorOutline color="error" fontSize="small" />
            </Tooltip>
          );
        }

        return <Box>{params.value ? `₹ ${params.value}` : "-"}</Box>;
      },
    },
    {
      field: "overtimeHours",
      headerName: "Overtime Hours",
      flex: 1,
      // disable editing if no overtime rate
      editable: (params) => Boolean(params.row?.overtimeHourlyRate),
      valueSetter: (value, row) => {
        const overtimeHours = value === "" || value == null ? "" : Number(value);
        const salary = Number(row.salary) || 0;
        const overtimeRate = Number(row.overtimeHourlyRate) || 0;

        const total = salary + overtimeHours * overtimeRate;

        return {
          ...row,
          overtimeHours,
          overtimeError: false,
          ...(row.paid && { paidAmount: total }),
        };
      },
      // edit mode
      renderEditCell: (params) => {
        const rate = params.row?.overtimeHourlyRate;
        if (!rate) {
          return (
            <Typography variant="body2" color="text.secondary">
              NA
            </Typography>
          );
        }

        return (
          <TextField
            type="number"
            size="small"
            fullWidth
            placeholder={`₹ ${rate}/hour`}
            error={Boolean(params.row.overtimeError)}
            value={params.value || ""}
            onChange={(e) =>
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: e.target.value,
              })
            }
          />
        );
      },

      renderCell: (params) => {
        const rate = params.row?.overtimeHourlyRate;

        if (!rate) return "NA";

        if (params.row.overtimeError) {
          return (
            <Tooltip title={params.row.overtimeError}>
              <ErrorOutline color="error" fontSize="small" />
            </Tooltip>
          );
        }

        if (!params.value) return "—";

        return `${params.value} hrs (₹ ${rate}/hour)`;
      },
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
      editable: false,
      valueGetter: (value, row) => {
        const salary = Number(row.salary) || 0;
        const overtimeHours = Number(row.overtimeHours) || 0;
        const overtimeHourlyRate = Number(row.overtimeHourlyRate) || 0;
        return salary + overtimeHours * overtimeHourlyRate;
      },
      renderCell: (params) => `₹ ${params.value.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`,
    },

    // ✅ PAID COLUMN (MAIN LOGIC)
    {
      field: "paid",
      headerName: "Paid",
      flex: 0.6,
      sortable: false,
      editable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(params.value)}
          onChange={(e) => {
            const checked = e.target.checked;
            const updateRow = (id, updates) => {
              setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...updates } : row)));
            };

            // ❌ Unchecked → reset fields
            if (!checked) {
              updateRow(params.id, {
                paid: false,
                accountId: null,
                accountName: null,
                paidAmount: null,
              });
              return;
            }

            // ✅ Checked → fetch accounts + compute total
            const salary = Number(params.row.salary) || 0;
            const overtimeHours = Number(params.row.overtimeHours) || 0;
            const overtimeRate = Number(params.row.overtimeHourlyRate) || 0;
            const total = salary + overtimeHours * overtimeRate;

            updateRow(params.id, {
              paid: true,
              accountId: selectedAccount?.id || null,
              accountName: selectedAccount?.name || null,
              paidAmount: total,
            });
          }}
        />
      ),
    },
    {
      field: "accountId",
      headerName: "Account",
      flex: 1,
      editable: (params) => params.row.paid,

      valueGetter: (value, row) => row.accountId, // important for refresh

      renderCell: (params) => {
        if (!params.row.paid) return "—";

        if (params.row.accountError) {
          return (
            <Tooltip title="Account is required">
              <ErrorOutline color="error" fontSize="small" />
            </Tooltip>
          );
        }

        return params.row.accountName || "Select account";
      },

      renderEditCell: (params) => {
        if (!params.row.paid) return "—";

        return (
          <SelectField
            size="small"
            value={params.value ? { id: params.value, name: params.row.accountName } : null}
            getData={AccountService.getAdminAndUserAccounts}
            getDataStaticFilters={{ hasViewAccount, user, companyAdminId }}
            onChange={(val) => {
              params.api.updateRows([
                {
                  id: params.id,
                  accountId: val?.id,
                  accountName: val?.name,
                },
              ]);

              return params.api.setEditCellValue({
                id: params.id,
                field: "accountId",
                value: val?.id,
              });
            }}
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      field: "paidAmount",
      headerName: "Paid Amount",
      flex: 1,
      editable: (params) => params.row.paid,
      renderCell: (params) => {
        if (!params.row.paid) return "—";

        if (params.row.paidAmountError) {
          return (
            <Tooltip title="Paid amount is required">
              <ErrorOutline color="error" fontSize="small" />
            </Tooltip>
          );
        }

        return params.value ? `₹ ${params.value}` : "Enter amount";
      },

      renderEditCell: (params) => {
        if (!params.row.paid) return "—";

        return (
          <TextField
            type="number"
            size="small"
            fullWidth
            value={params.value || ""}
            onChange={(e) =>
              params.api.setEditCellValue({
                id: params.id,
                field: "paidAmount",
                value: e.target.value,
              })
            }
          />
        );
      },
    },
  ];

  return headers;
};

export const getBulkWorkTableColumns = ({ validateOvertimeHours }) => [
  { field: "workerName", headerName: "Name", flex: 1.4, editable: false },
  { field: "designation", headerName: "Designation", flex: 1, editable: false },
  {
    field: "wage",
    headerName: "Wage",
    flex: 1,
    valueSetter: (value, row) => {
      const wage = Number(value) || 0;
      return {
        ...row,
        wage,
        wageError: wage > 0 ? "" : "Wage is required",
      };
    },
    renderCell: (params) => {
      if (params.row.wageError) {
        return (
          <Tooltip title={params.row.wageError}>
            <ErrorOutline color="error" fontSize="small" />
          </Tooltip>
        );
      }
      return `₹ ${Number(params.value || 0).toFixed(2)}`;
    },
    renderEditCell: (params) => (
      <TextField
        type="number"
        size="small"
        fullWidth
        value={params.value ?? ""}
        onChange={(e) =>
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: e.target.value,
          })
        }
      />
    ),
  },
  {
    field: "overtimeHours",
    headerName: "Overtime Hours",
    flex: 1.1,
    editable: (params) => Boolean(params.row?.overtimeHourlyRate),
    valueSetter: (value, row) => {
      const overtimeHours = value === "" || value == null ? "" : Number(value);
      const noRateError =
        Number(row.overtimeHourlyRate) <= 0 && Number(overtimeHours || 0) > 0 ? "No overtime rate for this worker" : "";
      return {
        ...row,
        overtimeHours,
        overtimeError: noRateError || validateOvertimeHours(overtimeHours),
      };
    },
    renderEditCell: (params) => {
      const rate = Number(params.row?.overtimeHourlyRate) || 0;
      if (!rate) return <Box sx={{ px: 1 }}>NA</Box>;
      return (
        <TextField
          type="number"
          size="small"
          fullWidth
          placeholder={`₹ ${rate}/hour`}
          value={params.value ?? ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
        />
      );
    },
    renderCell: (params) => {
      const rate = Number(params.row?.overtimeHourlyRate) || 0;
      if (!rate) return "NA";
      if (params.row.overtimeError) {
        return (
          <Tooltip title={params.row.overtimeError}>
            <ErrorOutline color="error" fontSize="small" />
          </Tooltip>
        );
      }
      if (!params.value) return "—";
      return `${params.value} hrs`;
    },
  },
  {
    field: "overtimeHourlyRate",
    headerName: "Rate",
    flex: 0.9,
    editable: false,
    renderCell: (params) => {
      const rate = Number(params.value) || 0;
      return rate ? `₹ ${rate.toFixed(2)}` : "NA";
    },
  },
  {
    field: "total",
    headerName: "Total",
    flex: 1,
    editable: false,
    valueGetter: (value, row) =>
      (Number(row.wage) || 0) + (Number(row.overtimeHours) || 0) * (Number(row.overtimeHourlyRate) || 0),
    renderCell: (params) => `₹ ${Number(params.value || 0).toFixed(2)}`,
  },
  {
    field: "paymentStatus",
    headerName: "Status",
    flex: 0.9,
    editable: false,
    valueGetter: (value, row) => (row.originalWork?.isPaid ? "Paid" : "Not Paid"),
  },
];

export const GroupWorkParticularsColumns = () => [
  {
    field: "name",
    headerName: "Particular Name",
    flex: 2,
    editable: false,
    renderCell: (params) => params.row.name || "-",
  },
  {
    field: "rate",
    headerName: "Rate",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: true,
    valueSetter: (value, row) => ({ ...row, rate: value }),
    renderCell: (params) => (params.value ? `₹ ${params.value}` : "-"),
  },
  {
    field: "quantity",
    headerName: "Quantity",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: true,
    valueSetter: (value, row) => ({ ...row, quantity: value }),
    renderCell: (params) => params.value || "-",
  },
  {
    field: "total",
    headerName: "Total",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: false,
    type: "number",
    valueGetter: (value, row) => {
      const qty = Number(row.quantity) || 0;
      const rate = Number(row.rate) || 0;
      return qty * rate;
    },
    renderCell: (params) => {
      const total = params.value;
      return total ? `₹ ${total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "-";
    },
  },
];
